"use client";

import Cal, { getCalApi } from "@calcom/embed-react";
import { useEffect, useMemo, useRef } from "react";
import { CAL_NAMESPACE, CAL_LINK, CAL_BRAND_COLOR } from "@/lib/config";

// ---------------------------------------------------------------------------
// Modal cal.com — ouvert depuis le CTA de la page VSL (profils qualifiés).
// Pur modal, sans titre : plein écran sur mobile, centré + arrondi sur desktop.
// Le clic sur le fond (ou ×) referme et revient à la vidéo.
// Prefill nom/email + UTM transmis à cal.com. À la réservation
// (`bookingSuccessful`), on notifie le parent via `onBooked`.
// ---------------------------------------------------------------------------

export function CalModal({
  open,
  onClose,
  onBooked,
  prefill,
  utms,
}: {
  open: boolean;
  onClose: () => void;
  onBooked: () => void;
  prefill?: { name?: string; email?: string };
  utms?: Record<string, string>;
}) {
  const onBookedRef = useRef(onBooked);
  onBookedRef.current = onBooked;

  useEffect(() => {
    let cancelled = false;
    (async function () {
      const cal = await getCalApi({ namespace: CAL_NAMESPACE });
      if (cancelled) return;
      cal("ui", {
        cssVarsPerTheme: {
          light: { "cal-brand": CAL_BRAND_COLOR },
          dark: { "cal-brand": CAL_BRAND_COLOR },
        },
        hideEventTypeDetails: false,
        layout: "month_view",
      });
      cal("on", {
        action: "bookingSuccessful",
        callback: () => onBookedRef.current(),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Config transmise à cal.com : layout + prefill + UTM (nettoyés des vides).
  const config = useMemo(() => {
    const c: Record<string, string> = {
      layout: "month_view",
      useSlotsViewOnSmallScreen: "true",
    };
    if (prefill?.name) c.name = prefill.name;
    if (prefill?.email) c.email = prefill.email;
    if (utms) for (const [k, v] of Object.entries(utms)) if (v) c[k] = v;
    return c;
  }, [prefill?.name, prefill?.email, utms]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Réserver un entretien"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative h-[100dvh] w-full max-w-[920px] overflow-hidden bg-white shadow-2xl sm:h-[660px] sm:max-h-[92dvh] sm:rounded-2xl"
      >
        {/* Fermer — revient à la vidéo */}
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-3 right-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/90 text-xl text-ink shadow-md backdrop-blur transition hover:bg-white"
        >
          ×
        </button>

        <Cal
          namespace={CAL_NAMESPACE}
          calLink={CAL_LINK}
          style={{ width: "100%", height: "100%", overflow: "scroll" }}
          config={config}
        />
      </div>
    </div>
  );
}
